import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { appAlert, appConfirm } from "../../utils/appPopup";
import {
  NotificationsService,
  mapNotificationToRoute,
} from "../../services/notifications.service";
import {
  WarehouseNotificationsService,
  mapWarehouseNotificationToRoute,
} from "../../services/warehouseNotifications.service";
import { connectSocket } from "../../services/notificationsSocket";
import { getAuthUser } from "../../pages/AdminAuth/components/authStore";
import { ensureWarehouseContext } from "../../services/warehouseContext.service";

const ALERT_TYPE = "DRIVER_AUTO_ASSIGN_FAILED_AFTER_PAYMENT";
const POLL_MS = 10000;

function clean(value) {
  return String(value || "").trim();
}

function upper(value) {
  return clean(value).toUpperCase();
}

function getMeta(item = {}) {
  return item?.meta && typeof item.meta === "object" ? item.meta : {};
}

function getTitle(item = {}) {
  return clean(item?.title || item?.event_type || item?.eventType || item?.type || "Driver assignment failed");
}

function getMessage(item = {}) {
  const meta = getMeta(item);
  return clean(
    meta?.displayAction ||
      item?.message ||
      item?.body ||
      item?.description ||
      meta?.reason ||
      "Payment is successful, but driver auto assignment failed. Manual assignment is required."
  );
}

function getOrderNumber(item = {}) {
  const meta = getMeta(item);
  return clean(
    meta?.orderNumber ||
      meta?.order_number ||
      item?.orderNumber ||
      item?.order_number ||
      item?.entityNumber ||
      item?.entity_number ||
      meta?.orderId ||
      meta?.order_id ||
      item?.entity_id ||
      item?.entityId
  );
}

function getWarehouseName(item = {}) {
  const meta = getMeta(item);
  return clean(meta?.warehouseName || meta?.warehouse_name || item?.warehouseName || item?.warehouse_name);
}

function getWarehouseId(item = {}) {
  const meta = getMeta(item);
  return clean(
    meta?.warehouseId ||
      meta?.warehouse_id ||
      item?.warehouseId ||
      item?.warehouse_id ||
      item?.selectedWarehouseId ||
      item?.assignedWarehouseId
  );
}

function getReason(item = {}) {
  const meta = getMeta(item);
  return clean(
    meta?.reason ||
      meta?.failureReason ||
      meta?.failure_reason ||
      meta?.dispatchMessage ||
      meta?.dispatch_message ||
      item?.reason ||
      item?.failureReason ||
      "No eligible driver is available right now."
  );
}

function getSeenKey(mode, item = {}) {
  const meta = getMeta(item);
  const rawKey = clean(
    item?.id ||
      item?._id ||
      item?.dedupe_key ||
      item?.dedupeKey ||
      item?.entity_id ||
      item?.entityId ||
      meta?.orderId ||
      meta?.order_id ||
      `${item?.type || "dispatch-failed"}:${item?.created_at || item?.createdAt || Date.now()}`
  );

  return `dispatch_auto_assign_failed_popup_seen:${mode}:${rawKey}`;
}

function alreadySeen(mode, item) {
  try {
    return localStorage.getItem(getSeenKey(mode, item)) === "1";
  } catch (_err) {
    return false;
  }
}

function markSeen(mode, item) {
  try {
    localStorage.setItem(getSeenKey(mode, item), "1");
  } catch (_err) {
    // ignore private-mode/localStorage errors
  }
}

function isDriverAssignmentFailurePopup(item = {}) {
  const meta = getMeta(item);
  const key = [
    item?.type,
    item?.event_type,
    item?.eventType,
    item?.title,
    item?.message,
    item?.body,
    item?.description,
    meta?.alertType,
    meta?.alert_type,
    meta?.popupEvent,
    meta?.popup_event,
    meta?.popupEventType,
    meta?.popup_event_type,
    meta?.eventType,
    meta?.event_type,
    meta?.deliveryAssignmentStatus,
    meta?.delivery_assignment_status,
    meta?.dispatchStatus,
    meta?.dispatch_status,
    meta?.displayAction,
    meta?.reason,
  ]
    .map((value) => (typeof value === "object" ? JSON.stringify(value) : clean(value)))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const explicitAlert =
    upper(meta?.alertType) === ALERT_TYPE ||
    upper(meta?.alert_type) === ALERT_TYPE ||
    upper(meta?.popupEventType) === ALERT_TYPE ||
    upper(meta?.popup_event_type) === ALERT_TYPE ||
    upper(item?.type) === "ORDER_PAYMENT_SUCCESS_DRIVER_ASSIGNMENT_FAILED" ||
    upper(item?.type) === "WAREHOUSE_ORDER_DRIVER_ASSIGNMENT_FAILED";

  if (explicitAlert) return true;

  return (
    (key.includes("payment") || upper(meta?.paymentStatus) === "PAID") &&
    (key.includes("driver") || key.includes("dispatch") || key.includes("delivery partner")) &&
    (key.includes("auto assign") || key.includes("assignment")) &&
    (key.includes("fail") || key.includes("manual"))
  );
}

function pickItems(response, mode) {
  if (mode === "warehouse") {
    return Array.isArray(response?.items) ? response.items : Array.isArray(response?.data) ? response.data : [];
  }

  return Array.isArray(response?.data) ? response.data : Array.isArray(response?.items) ? response.items : [];
}

function isUnread(item = {}) {
  return !(item?.is_read || item?.isRead || item?.read_at || item?.readAt);
}

function buildPopupMessage(item = {}) {
  const orderNumber = getOrderNumber(item);
  const warehouseName = getWarehouseName(item);
  const message = getMessage(item);
  const reason = getReason(item);

  return [
    message,
    orderNumber ? `Order: ${orderNumber}` : "",
    warehouseName ? `Warehouse: ${warehouseName}` : "",
    reason ? `Reason: ${reason}` : "",
    "Manual driver assignment is required now.",
  ]
    .filter(Boolean)
    .join("\n");
}

export default function DispatchAutoAssignFailurePopup({ mode = "admin" }) {
  const navigate = useNavigate();
  const userRef = useRef(getAuthUser() || {});
  const popupOpenRef = useRef(false);

  const openTarget = async (item) => {
    const target =
      mode === "warehouse"
        ? mapWarehouseNotificationToRoute(item)
        : mapNotificationToRoute(item);

    if (!target) return;

    const warehouseId = getWarehouseId(item);
    if (target.includes("/admin/dashboard/customer-orders")) {
      try {
        await ensureWarehouseContext(warehouseId);
      } catch (err) {
        console.error("Failed to establish warehouse context for manual assignment:", err);
        appAlert(
          err?.message || "Unable to open the warehouse right now. Please try again.",
          {
            title: "Warehouse switch failed",
            danger: true,
            confirmText: "OK",
          }
        );
        return;
      }
    }

    navigate(target);
  };

  const showPopup = async (item) => {
    if (!item || popupOpenRef.current || alreadySeen(mode, item)) return;

    popupOpenRef.current = true;
    markSeen(mode, item);

    try {
      const openOrder = await appConfirm(buildPopupMessage(item), {
        title: getTitle(item),
        confirmText: "Open order",
        cancelText: "Later",
        danger: true,
      });

      if (openOrder) await openTarget(item);
    } finally {
      popupOpenRef.current = false;
    }
  };

  const consumeNotifications = (list = []) => {
    const candidate = list.find(
      (item) => isUnread(item) && isDriverAssignmentFailurePopup(item) && !alreadySeen(mode, item)
    );

    if (candidate) {
      void showPopup(candidate);
    }
  };

  const poll = async () => {
    try {
      if (mode === "warehouse") {
        const res = await WarehouseNotificationsService.list({ page: 1, limit: 20 });
        consumeNotifications(pickItems(res, mode));
        return;
      }

      const res = await NotificationsService.list(userRef.current, {
        isRead: false,
        limit: 20,
        offset: 0,
      });
      consumeNotifications(pickItems(res, mode));
    } catch (err) {
      console.error("Failed to poll driver assignment popup notification:", err);
    }
  };

  useEffect(() => {
    void poll();
    const timer = window.setInterval(poll, POLL_MS);
    return () => window.clearInterval(timer);
  }, [mode]);

  useEffect(() => {
    if (mode !== "admin") return undefined;

    const socket = connectSocket();
    if (!socket) return undefined;

    const handleNotification = (data) => {
      if (isUnread(data) && isDriverAssignmentFailurePopup(data)) {
        void showPopup(data);
      }
    };

    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [mode]);

  return null;
}
